/**
 * Horizontal phase timeline for container detail.
 * Shows only the 5 active ShipmentStatus values (DRAFT, BOOKED, IN_TRANSIT, ARRIVED, DELIVERED).
 * No legacy sub-steps. ON_HOLD and CANCELLED map to current phase. Overflow-x, scroll to current.
 */

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Edit05, Package, Truck01, MarkerPin01, Home02 } from "@untitledui/icons";
import type { Container } from "@/types/container";
import type { BackendActivity } from "@/data/api/activities";
import { CONTAINER_PHASE_IDS } from "@/data/container-phases";
import type { ContainerPhaseId } from "@/data/container-phases";

const PHASE_ICONS: Record<ContainerPhaseId, typeof Package> = {
    draft: Edit05,
    booked: Package,
    in_transit: Truck01,
    arrived: MarkerPin01,
    delivered: Home02,
};

/** One step per active phase (0–4). Descriptions use active-value keys only, no legacy. */
const PHASE_DESCRIPTION_KEYS: Record<ContainerPhaseId, string> = {
    draft: "detail.info.phaseTimeline.activeDesc.draft",
    booked: "detail.info.phaseTimeline.activeDesc.booked",
    in_transit: "detail.info.phaseTimeline.activeDesc.inTransit",
    arrived: "detail.info.phaseTimeline.activeDesc.arrived",
    delivered: "detail.info.phaseTimeline.activeDesc.delivered",
};

/** Maps container status to timeline step index (0–4). Only active values. */
function statusToStepIndex(status: Container["status"]): number {
    switch (status) {
        case "Draft":
            return 0;
        case "Booked":
            return 1;
        case "In Transit":
        case "On Hold":
            return 2;
        case "Arrived":
            return 3;
        case "Delivered":
        case "Cancelled":
            return 4;
        default:
            return 0;
    }
}

export interface ContainerPhaseTimelineProps {
    container: Container;
    activities?: BackendActivity[];
    className?: string;
}

export function ContainerPhaseTimeline({ container, activities = [], className = "" }: ContainerPhaseTimelineProps) {
    const { t } = useTranslation(["pages:containers", "common"]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const currentStepRef = useRef<HTMLDivElement>(null);

    const currentStepIndex = useMemo(() => statusToStepIndex(container.status), [container.status]);

    useEffect(() => {
        const timer = setTimeout(() => {
            currentStepRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }, 300);
        return () => clearTimeout(timer);
    }, [currentStepIndex]);

    const actorLabel =
        activities.find(
            (a) =>
                (a.activity_type === "status_changed" ||
                    a.activity_type === "expedition_status_changed" ||
                    a.activity_type === "phase_transition") &&
                a.actor_name
        )?.actor_name ?? "system";

    return (
        <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
            <h2 className="text-lg font-semibold text-primary mb-4">
                {t("detail.info.phaseTimeline.title", "Shipment progress")}
            </h2>
            <div
                ref={scrollContainerRef}
                className="overflow-x-auto overflow-y-hidden pb-2 -mx-1 px-1 scroll-smooth"
                style={{ scrollbarGutter: "stable" }}
            >
                <div className="flex items-stretch gap-0 min-w-max">
                    {CONTAINER_PHASE_IDS.map((phaseId, stepIndex) => {
                        const isCompleted = stepIndex < currentStepIndex;
                        const isCurrent = stepIndex === currentStepIndex;
                        const isPast = stepIndex <= currentStepIndex;
                        const PhaseIcon = PHASE_ICONS[phaseId];

                        return (
                            <div key={phaseId} className="flex items-stretch shrink-0">
                                <div
                                    ref={stepIndex === currentStepIndex ? currentStepRef : undefined}
                                    className={`flex items-start gap-3 shrink-0 w-52 py-2 px-4 rounded-lg border border-secondary ${
                                        isCurrent
                                            ? "bg-brand-50 dark:bg-brand-950/50 border-brand-200 dark:border-brand-800"
                                            : "bg-primary"
                                    } ${stepIndex > 0 ? "ml-2" : "ml-0"}`}
                                >
                                    <span className="mt-0.5 shrink-0">
                                        {isCompleted ? (
                                            <CheckCircle className="h-5 w-5 text-success-600 dark:text-success-400" />
                                        ) : (
                                            <PhaseIcon
                                                className={`h-5 w-5 ${
                                                    isCurrent
                                                        ? "text-brand-600 dark:text-brand-400"
                                                        : "text-quaternary"
                                                }`}
                                            />
                                        )}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-primary">
                                            {t(`detail.info.phaseTimeline.phases.${phaseId}`)}
                                        </p>
                                        <p className="text-xs text-tertiary mt-0.5">
                                            {t(PHASE_DESCRIPTION_KEYS[phaseId])}
                                        </p>
                                        {isPast && (
                                            <p className="text-xs text-tertiary mt-1">
                                                {t("detail.info.phaseTimeline.by", "By")}: {actorLabel}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {stepIndex < CONTAINER_PHASE_IDS.length - 1 && (
                                    <div className="shrink-0 w-4 flex items-center justify-center">
                                        <div className="h-px w-3 bg-quaternary" aria-hidden />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
